import React, { useCallback, useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { useHistory, useParams } from "react-router-dom";
import { observer } from "mobx-react";
import { Step } from "src/screens/process/step/Step";
import { Person } from "src/api/Notification";
import { SubmitHandler, useForm } from "react-hook-form";
import { runInAction } from "mobx";
import { Route, selectRoute } from "src/config/routes";
import { useTranslation } from "react-i18next";
import "yup-phone-lite";
import parsePhoneNumber, { AsYouType, isValidPhoneNumber } from "libphonenumber-js";
import { OptionalInput } from "src/components/optionalInput/OptionalInput";
import { GENDER } from "src/api/Notification";

export interface ContactFormFields {
    id?: string;
    salutation: string;
    name1: string;
    name2: string;
    email: string;
    mobile: string;
    reachability: string[];
}

const REACHABILITY_TIMES = [
    { from: "08:00:00", to: "10:00:00" },
    { from: "10:00:00", to: "12:00:00" },
    { from: "14:00:00", to: "16:00:00" },
    { from: "16:00:00", to: "18:00:00" }
];

const ContactBase = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { processId } = useParams<{ processId: string; stepId: string }>();
    const { easyContactStore, uiStore } = useContext(RootStoreContext);
    const { nextStep } = easyContactStore.stepNavigation;

    const salutationMale = t("screens.contact.salutation.male");
    const salutationFemale = t("screens.contact.salutation.female");
    const salutationDivers = t("screens.contact.salutation.divers");
    const salutationCompany = t("screens.contact.salutation.company");
    const hasEmailFromBackend: boolean = easyContactStore.backendContactInformation?.email ? true : false;
    const hasMobileFromBackend: boolean = easyContactStore.backendContactInformation?.mobile ? true : false;

    const [useEmailFromBackend, setUseEmailFromBackend] = useState<boolean>(
        easyContactStore.notification.contactperson?.email ? false : hasEmailFromBackend ? true : false
    );

    const [useMobileFromBackend, setUseMobileFromBackend] = useState<boolean>(
        easyContactStore.notification.contactperson?.mobile ? false : hasMobileFromBackend ? true : false
    );

    const [name1Error, setName1Error] = useState<string | undefined>(undefined);
    const [name2Error, setName2Error] = useState<string | undefined>(undefined);
    const [mailError, setMailError] = useState<string | undefined>(undefined);
    const [mobileError, setMobileError] = useState<string | undefined>(undefined);

    let initialSalutationGenderOptionValue = `${salutationMale};${GENDER.MALE}`;
    if (
        easyContactStore.backendContactInformation?.gender !== undefined &&
        easyContactStore.backendContactInformation?.gender !== null
    ) {
        switch (easyContactStore.backendContactInformation?.gender) {
            case GENDER.MALE:
                initialSalutationGenderOptionValue = `${salutationMale};${GENDER.MALE}`;
                break;
            case GENDER.FEMALE:
                initialSalutationGenderOptionValue = `${salutationFemale};${GENDER.FEMALE}`;
                break;
            case GENDER.LEGAL_PERSON:
                initialSalutationGenderOptionValue = `${salutationCompany};${GENDER.LEGAL_PERSON}`;
                break;
            case GENDER.NEUTRAL:
                initialSalutationGenderOptionValue = `${salutationDivers};${GENDER.NEUTRAL}`;
                break;
        }
    }

    const { register, handleSubmit, setValue, watch } = useForm<ContactFormFields>({
        mode: "onBlur",
        defaultValues: {
            salutation: easyContactStore.notification.contactperson?.salutation ?? initialSalutationGenderOptionValue,
            name1:
                easyContactStore.notification.contactperson?.name1 ?? easyContactStore.backendContactInformation?.name1,
            name2:
                easyContactStore.notification.contactperson?.name2 ?? easyContactStore.backendContactInformation?.name2,
            email: easyContactStore.notification.contactperson?.email,
            mobile: easyContactStore.notification.contactperson?.mobile,
            id: easyContactStore.backendContactInformation?.id
        }
    });

    const watchName1 = watch("name1");
    const watchName2 = watch("name2");
    const watchSalutation = watch("salutation");

    const isCompanyContact =
        watchSalutation === t("screens.contact.salutation.company") ||
        watchSalutation === `${salutationCompany};${GENDER.LEGAL_PERSON}`;

    useEffect(() => {
        if (watchName1 && watchName1.length > 0) {
            setName1Error(undefined);
        }
    }, [watchName1]);

    useEffect(() => {
        if (watchName2 && watchName2.length > 0) {
            setName2Error(undefined);
        }
    }, [watchName2]);

    // https://stackoverflow.com/a/46181/12460180
    const isValidEmailAddress = useCallback((email: string) => {
        const re =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(email.toLowerCase());
    }, []);

    const onSubmit: SubmitHandler<ContactFormFields> = useCallback(
        (data) => {
            setName1Error(undefined);
            setName2Error(undefined);
            setMobileError(undefined);
            setMailError(undefined);

            const reachabilityArray: { from: string; to: string }[] = [];
            if (data.reachability) {
                data.reachability.forEach((reachability, index) => {
                    if (reachability) {
                        reachabilityArray.push({
                            from: REACHABILITY_TIMES[index].from,
                            to: REACHABILITY_TIMES[index].to
                        });
                    }
                });
            }

            let hasFormErrors = false;

            if (!data.name1) {
                setName1Error(t("form.required_field"));
                hasFormErrors = true;
            }

            if (!data.name2) {
                if (!isCompanyContact) {
                    setName2Error(t("form.required_field"));
                    hasFormErrors = true;
                }
            }

            let personEmail = undefined;
            if (hasEmailFromBackend && useEmailFromBackend) {
                personEmail = undefined;
            } else {
                if (!data.email) {
                    setMailError(t("form.required_field"));
                    hasFormErrors = true;
                } else if (!isValidEmailAddress(data.email)) {
                    setMailError(t("form.email_format_invalid"));
                    hasFormErrors = true;
                } else {
                    personEmail = data.email;
                }
            }

            let personMobile = undefined;
            if (hasMobileFromBackend && useMobileFromBackend) {
                personMobile = undefined;
            } else {
                if (!data.mobile) {
                    setMobileError(t("form.required_field"));
                    hasFormErrors = true;
                } else if (!isValidPhoneNumber(data.mobile, "CH")) {
                    setMobileError(t("form.phone_number_invalid"));
                    hasFormErrors = true;
                } else {
                    personMobile = data.mobile;
                }
            }

            if (hasFormErrors) {
                return;
            }

            let internationalPhoneNumber = undefined;

            if (personMobile) {
                const formPhoneNumber = parsePhoneNumber(personMobile, "CH");
                internationalPhoneNumber = formPhoneNumber?.formatInternational();
            }

            const salutationAndGender = data.salutation.split(";");

            const personSalutation = salutationAndGender[0];
            let personGender = 0;

            if (salutationAndGender.length > 1) {
                personGender = Number(salutationAndGender[1]);
            } else if (
                easyContactStore.backendContactInformation?.gender !== undefined &&
                easyContactStore.backendContactInformation?.gender !== null
            ) {
                personGender = easyContactStore.backendContactInformation?.gender;
            }

            const personObject: Person = {
                id: data.id,
                salutation: personSalutation,
                gender: personGender,
                name1: data.name1,
                name2: data.name2,
                email: personEmail,
                mobile: internationalPhoneNumber,
                reachability: reachabilityArray
            };

            runInAction(() => {
                easyContactStore.setSendingPerson(personObject);
            });

            if (nextStep) {
                history.push(
                    selectRoute(Route.step, { processId, stepType: String(nextStep.type), stepId: nextStep.id })
                );
            }
        },
        [
            history,
            easyContactStore,
            nextStep,
            processId,
            hasEmailFromBackend,
            useEmailFromBackend,
            hasMobileFromBackend,
            useMobileFromBackend,
            t,
            isValidEmailAddress,
            isCompanyContact
        ]
    );

    const checkedReachabilityValues = REACHABILITY_TIMES.map(() => false);

    if (easyContactStore.notification.contactperson?.reachability) {
        easyContactStore.notification.contactperson?.reachability.forEach((reachability, index) => {
            const indexReachability = REACHABILITY_TIMES.findIndex(
                (standardReachability) => standardReachability.from === reachability.from
            );
            if (indexReachability !== -1) {
                checkedReachabilityValues[indexReachability] = true;
            }
        });
    }

    const reachabilityCheckboxes = REACHABILITY_TIMES.map((reachability, index) => {
        return (
            <UI.Form.Check inline={false} key={index}>
                <UI.Form.Check.Input
                    {...register(`reachability.${index}`)}
                    type="checkbox"
                    id={`reachability-${index}`}
                    defaultChecked={checkedReachabilityValues[index]}
                    disabled={false}
                    value={`${reachability.from} - ${reachability.to}`}
                />
                <UI.Form.Check.Label htmlFor={`reachability-${index}`}>
                    {`${reachability.from.substring(0, 5)} - ${reachability.to.substring(0, 5)}`}
                </UI.Form.Check.Label>
            </UI.Form.Check>
        );
    });

    const onBlurPhoneNumber = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const currentPhoneValue = e.target.value;
            const formattedNumber = new AsYouType("CH").input(currentPhoneValue);
            setValue("mobile", formattedNumber);

            setMobileError(undefined);
        },
        [setValue]
    );

    const onBlurEmailAddress = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setMailError(undefined);
    }, []);

    const setEmailFromBackendState = useCallback((e: any) => {
        const checkedValue = e.target.value === "true" ? true : false;
        setUseEmailFromBackend(checkedValue);
    }, []);

    const setMobileFromBackendState = useCallback((e: any) => {
        const checkedValue = e.target.value === "true" ? true : false;
        setUseMobileFromBackend(checkedValue);
    }, []);

    return (
        <Step>
            <UI.Container className="pt-4">
                <UI.Row>
                    <UI.Col md={uiStore.defaultColumnSize.md}>
                        <UI.Form onSubmit={handleSubmit(onSubmit)}>
                            <UI.Row>
                                <UI.Col>
                                    <UI.Input
                                        label={`${t("screens.contact.salutation.title")} *`}
                                        {...register("salutation")}
                                        defaultValue={initialSalutationGenderOptionValue}
                                        as="select"
                                    >
                                        <option value={`${salutationMale};${GENDER.MALE}`}>{salutationMale}</option>
                                        <option value={`${salutationFemale};${GENDER.FEMALE}`}>
                                            {salutationFemale}
                                        </option>
                                        <option value={`${salutationDivers};${GENDER.NEUTRAL}`}>
                                            {salutationDivers}
                                        </option>
                                        <option value={`${salutationCompany};${GENDER.LEGAL_PERSON}`}>
                                            {salutationCompany}
                                        </option>
                                    </UI.Input>
                                </UI.Col>
                                <UI.Col></UI.Col>
                            </UI.Row>
                            <UI.Row>
                                <UI.Col xs={12}>
                                    <UI.Input
                                        {...register("name1")}
                                        label={`${
                                            isCompanyContact
                                                ? t("screens.contact.firstname_company")
                                                : t("screens.contact.firstname")
                                        } *`}
                                        type="text"
                                        name="name1"
                                        defaultValue={easyContactStore.backendContactInformation?.name1}
                                        errorMsg={name1Error}
                                    />
                                </UI.Col>
                                <UI.Col xs={12}>
                                    <UI.Input
                                        {...register("name2")}
                                        name="name2"
                                        defaultValue={easyContactStore.backendContactInformation?.name2}
                                        label={`${
                                            isCompanyContact
                                                ? t("screens.contact.lastname_company")
                                                : `${t("screens.contact.lastname")} *`
                                        }`}
                                        errorMsg={name2Error}
                                        type="text"
                                    />
                                </UI.Col>
                            </UI.Row>
                            <OptionalInput
                                inputLabel={`${t("screens.contact.mail")} *`}
                                valueFromBackend={easyContactStore.backendContactInformation?.email}
                                valueFromStorage={easyContactStore.notification.contactperson?.email}
                                useValueFromBackend={useEmailFromBackend}
                                onBlurInput={onBlurEmailAddress}
                                onChangeRadioInput={setEmailFromBackendState}
                                radioInputError={mailError}
                                name="email"
                                register={register}
                                errorMessage={mailError}
                                inputType="email"
                                radioInputPlaceholder={t("screens.contact.mail_placeholder")}
                            />
                            <div className="mt-3" />
                            <OptionalInput
                                inputLabel={`${t("screens.contact.mobile")} *`}
                                valueFromBackend={easyContactStore.backendContactInformation?.mobile}
                                valueFromStorage={easyContactStore.notification.contactperson?.mobile}
                                useValueFromBackend={useMobileFromBackend}
                                onBlurInput={onBlurPhoneNumber}
                                onChangeRadioInput={setMobileFromBackendState}
                                radioInputError={mobileError}
                                name="mobile"
                                register={register}
                                errorMessage={mobileError}
                                inputType="tel"
                                radioInputPlaceholder={t("screens.contact.mobile_placeholder")}
                            />
                            <div className="mt-3" />
                            <UI.Input id="reachability" type="checkbox" label={t("screens.contact.reachability")}>
                                <UI.Row>
                                    <UI.Col>{reachabilityCheckboxes.slice(0, 2)}</UI.Col>
                                    <UI.Col>{reachabilityCheckboxes.slice(2, 4)}</UI.Col>
                                </UI.Row>
                            </UI.Input>
                            <UI.Button type="submit" onClick={handleSubmit(onSubmit)} label={t("labels.continue")} />
                        </UI.Form>
                    </UI.Col>
                </UI.Row>
            </UI.Container>
        </Step>
    );
};

export const Contact = Sentry.withProfiler(observer(ContactBase));
